const FETCH_RETRY_DELAYS_MS = [500, 1000, 1500, 2000] as const

const waitForRetry = (ms: number, signal?: AbortSignal): Promise<void> => {
  if (ms <= 0) {
    return Promise.resolve()
  }

  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"))
      return
    }

    const timeoutId = window.setTimeout(() => {
      signal?.removeEventListener("abort", handleAbort)
      resolve()
    }, ms)

    const handleAbort = () => {
      window.clearTimeout(timeoutId)
      signal?.removeEventListener("abort", handleAbort)
      reject(new DOMException("Aborted", "AbortError"))
    }

    signal?.addEventListener("abort", handleAbort, { once: true })
  })
}

const isRetriableFetchStatus = (status: number): boolean => {
  return [408, 425, 429, 500, 502, 503, 504].includes(status)
}

export const fetchWithRetry = async (
  source: string,
  signal?: AbortSignal,
): Promise<Response> => {
  let lastError: unknown
  let lastStatus: number | null = null

  for (let attempt = 0; attempt <= FETCH_RETRY_DELAYS_MS.length; attempt += 1) {
    if (signal?.aborted) {
      throw new DOMException("Aborted", "AbortError")
    }

    try {
      const response = await fetch(source, { signal })

      if (response.ok) {
        return response
      }

      lastStatus = response.status

      if (
        !isRetriableFetchStatus(response.status) ||
        attempt === FETCH_RETRY_DELAYS_MS.length
      ) {
        break
      }
    } catch (error) {
      if (signal?.aborted) {
        throw error
      }

      lastError = error

      if (attempt === FETCH_RETRY_DELAYS_MS.length) {
        break
      }
    }

    await waitForRetry(FETCH_RETRY_DELAYS_MS[attempt], signal)
  }

  if (lastStatus !== null) {
    throw new Error(`Failed to fetch the selected file (${lastStatus})`)
  }

  throw lastError ?? new Error("Failed to fetch the selected file")
}

export const __testables__ = {
  FETCH_RETRY_DELAYS_MS,
  isRetriableFetchStatus,
}
